import React, { useRef, useEffect, useState } from "react";
import SvgComponent from "../../../_utils/SVGComponent";
import SVGComponent from "../../../_utils/SVGComponent";
import TickerAnimation from "../../TickerAnimation";
import {
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../../utils/configurationProvider";

// Suggestions of props for component while using the components -------------------------------------
/**
 * @typedef {((e: React.MouseEvent<HTMLButtonElement, MouseEvent>?) => void)} OnClickEventHandler
 */
/**
 * @typedef {("big" | "small")} DropdownType
 * @param {Object} props
 * @param {Array} props.options - Default options to show
 * @param {String} props.selected - Selected option value state
 * @param {Function} props.setSelected - State setter function for option value
 * @param {DropdownType} props.type - Dropdown type
 * @param {boolean} props.isOpen - State to open or close dropdown
 * @param {boolean} props.showSearch - State to display or hide search field
 * @param {String} props.placeholder - Default placeholder for dropdown
 * @param {String} props.searchPlaceholder - Default search placeholder
 * @param {boolean} props.disabled - Checks wheather dropdown is disabled or not
 * @param {Function} props.onOpen - Callback for when this dropdown is opened
 * @param {Function} props.onClose - Callback for when this dropdown is closed
 * @returns {JSX.Element}
 */

const MultiSelectDropDownComponent = ({
  options,
  selected = [],
  setSelected,
  isOpen,
  showSearch,
  placeholder,
  searchPlaceholder,
  disabled,
  type,
  onOpen,
  onClose,
}) => {
  const multiSelectDropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const dropdownOptionRef = useRef(null);
  const optionRefs = useRef([]);
  const [overflowFlags, setOverflowFlags] = useState([]);
  const [dropDownOptions, setDropDownOptions] = useState([]);
  const [dropdown, setDropdown] = useState(isOpen);
  const [showClearIcon, setShowClearIcon] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);

  const toggleItem = (val) => {
    if (selected.includes(val)) {
      setSelected(selected.filter((item) => item !== val));
    } else {
      setSelected([...selected, val]);
    }
  };

  useEffect(() => {
    setDropDownOptions(options);
  }, [options]);

  // Sync internal dropdown state with external isOpen prop
  useEffect(() => {
    setDropdown(isOpen);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        multiSelectDropdownRef.current &&
        !multiSelectDropdownRef.current?.contains(event.target)
      ) {
        setDropdown(false);
        onClose?.call();
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [onClose]);

  const handleChange = (e) => {
    e.stopPropagation();
    let opt = options.filter((val) => {
      return val.toLowerCase().includes(e.target.value.toLowerCase());
    });
    setDropDownOptions(opt);
    setShowClearIcon(
      e.target.value.trim().length > GlobalConfiguration?.lengthCheckZero,
    );
  };

  const getFontSize = (type) => {
    switch (type) {
      case "small":
        return "text-font-xs";
      case "big":
        return "text-font-md";
      default:
        return "";
    }
  };

  const getMaxHeight = (type) => {
    switch (type) {
      case "small":
        return "max-h-dropdown-option-height-sm";
      case "big":
        return "max-h-dropdown-option-height-md";
      default:
        return "";
    }
  };

  useEffect(() => {
    if (dropdown) {
      const flags = dropDownOptions?.map((_, i) => {
        const el = optionRefs.current[i];
        return el?.scrollWidth > el?.offsetWidth;
      });
      setOverflowFlags(flags);
    }
  }, [dropdown, dropDownOptions]);

  return (
    <div className="w-full">
      <div
        ref={multiSelectDropdownRef}
        className={`flex flex-col relative w-full ${dropdown ? "open" : ""} ${
          disabled
            ? "cursor-not-allowed opacity-light-opacity"
            : "cursor-pointer opacity-base-opacity"
        } ${getMaxHeight(type)}`}
        onClick={() => {
          if (!disabled) {
            if (dropdown) {
              setDropdown(false);
              setTimeout(() => {
                setOpenUpward(false);
              }, IntervalConfiguration.intervalMid);
              onClose?.call();
            } else {
              const containerRect = document
                .getElementById("root")
                ?.getBoundingClientRect();
              const cardRect =
                dropdownOptionRef?.current?.getBoundingClientRect();
              const willOverflowBottom =
                cardRect?.bottom > containerRect?.bottom &&
                cardRect?.top >= cardRect?.height;

              setOpenUpward(willOverflowBottom);
              setDropdown(true);
              onOpen?.call();
            }
          }
        }}
      >
        <div
          className={`bg-primary-nav-act ${getMaxHeight(type)} ${
            !disabled && !dropdown && "global-hover-white"
          } z-z-index-base relative flex items-center justify-between w-full px-padding-md py-padding-sm gap-gap-sm dropdown_header
    ${dropdown ? `${openUpward ? "rounded-b-border-radius-md shadow-box-shadow-right" : "dropdown_border_bottom_radius_none shadow-box-shadow-right"}` : `${openUpward ? "rounded-border-radius-md delay-[var(--animation-fast)]" : "dropdown_border-radius-md"}`}`}
        >
          <span
            className={`${getFontSize(type)} truncate select-none`}
            style={{
              color:
                selected?.length === GlobalConfiguration?.lengthCheckZero
                  ? "var(--text-secondary)"
                  : "var(--text-primary)",
            }}
          >
            {selected?.length === GlobalConfiguration?.lengthCheckZero
              ? placeholder
              : `${selected?.length} labels selected`}
          </span>
          <div className="flex flex-row gap-gap-sm items-center flex-shrink-0">
            <img
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) {
                  setSelected([]);
                }
              }}
              src="https://q2pay.in/new_assets/common/create_flow/close.svg"
              alt="down"
              className="h-icon-xs w-icon-xs"
              style={{
                display:
                  selected?.length > GlobalConfiguration?.lengthCheckZero
                    ? "flex"
                    : "none",
              }}
            />
            <div
              style={{
                display:
                  selected?.length > GlobalConfiguration?.lengthCheckZero
                    ? "flex"
                    : "none",
              }}
              className="border-l max-h-dropdown-divider-height h-screen items-center border-primary-bg"
            ></div>
            <div
              className={`transition-transform duration-animation-fast ease-in-out ${
                dropdown ? "rotate-half" : "rotate-base"
              }`}
            >
              <SvgComponent
                url={
                  "https://q2pay.in/new_assets/common/arrows/arrow_down_white.svg"
                }
                color="var(--text-primary)"
                width="var(--icon-xs)"
                height="var(--icon-xs)"
              />
            </div>
          </div>
        </div>

        <div className="relative">
          <div
            className={`bg-primary-nav-act ${dropdown ? `${openUpward ? "shadow-box-shadow-right-top" : "shadow-box-shadow-right-bottom"}` : ""} ${openUpward ? `${type === "small" ? "bottom-[var(--dropdown-option-height-sm)]" : "bottom-[var(--dropdown-option-height-md)]"} flex flex-col-reverse rounded-t-border-radius-md` : "rounded-b-border-radius-md"}  absolute z-z-index-dropdown overflow-hidden w-width-full `}
            style={{
              maxHeight: dropdown
                ? type == "big"
                  ? "var(--max-animation-height-big-ddl)"
                  : "var(--max-animation-height-small-ddl)"
                : "0px",
              transition: "max-height var(--animation-fast) ease-in-out",
            }}
          >
            <div
              className="border-primary-bg border-t mx-margin-sm transition-all duration-[var(--animation-fast)]"
              style={{
                opacity: !dropdown
                  ? "var(--opacity-none)"
                  : "var(--base-opacity)",
              }}
            ></div>
            {showSearch && (
              <div className="bg-primary-nav-act px-padding-sm pt-padding-sm w-full">
                <div
                  className={`rounded-border-radius-md flex items-center px-padding-sm py-padding-sm opacity-base-opacity bg-primary-bg ${getMaxHeight(
                    type,
                  )}`}
                >
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      searchInputRef.current?.focus();
                    }}
                  >
                    <SVGComponent
                      url={
                        "https://q2pay.in/new_assets/common/view_all/search_white.svg"
                      }
                      color="var(--text-primary)"
                      height={"var(--icon-sm)"}
                      width={"var(--icon-sm)"}
                    />
                  </div>
                  <input
                    ref={searchInputRef}
                    className={`w-full outline-none bg-transparent text-left ${getFontSize(
                      type,
                    )} font-normal ml-margin-sm text-text-secondary border-text-primary placeholder-text-secondary`}
                    type="text"
                    onChange={handleChange}
                    onClick={(e) => e.stopPropagation()}
                    placeholder={searchPlaceholder}
                  />
                  {showClearIcon && (
                    <img
                      src="https://q2pay.in/new_assets/common/dashboard/close_with_white_circle.svg"
                      alt="clear"
                      className="h-icon-sm w-icon-sm cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (searchInputRef.current) {
                          searchInputRef.current.value = "";
                        }
                        setDropDownOptions(options);
                        setShowClearIcon(false);
                      }}
                    />
                  )}
                </div>
              </div>
            )}
            <div
              ref={dropdownOptionRef}
              className={`flex flex-col pt-padding-sm pl-padding-sm pb-padding-base ${
                type == "big"
                  ? "max-h-max-overflow-height-big-ddl-options"
                  : "max-h-max-overflow-height-small-ddl-options"
              } bg-primary-nav-act w-full rounded-b-border-radius-md overflow-auto border-t-border-base cursor-pointer gap-gap-sm`}
            >
              <div className="flex flex-col pr-padding-sm bg-primary-nav-act w-full overflow-auto gap-gap-sm mb-margin-sm">
                {dropDownOptions?.map((val, index) => {
                  return (
                    <div
                      key={index}
                      title={overflowFlags[index] ? val : ""}
                      className={`relative max-w-full gap-gap-sm ${getMaxHeight(
                        type,
                      )} z-z-index-base global-hover-white py-padding-sm px-padding-sm truncate text-text-primary flex justify-between items-center rounded-border-radius-md flex-shrink-0 ${getFontSize(
                        type,
                      )} ${selected.includes(val) ? "bg-primary-nav" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleItem(val);
                      }}
                    >
                      <p
                        ref={(el) => (optionRefs.current[index] = el)}
                        className="truncate"
                      >
                        <TickerAnimation text={val} />
                      </p>
                      <img
                        src={
                          selected.includes(val)
                            ? "https://q2pay.in/new_assets/common/approval/selected_with_green_circle.svg"
                            : "https://q2pay.in/new_assets/common/approval/select_all_with_white_filled_circle.svg"
                        }
                        alt="Checkmark"
                        className="h-icon-sm w-icon-sm"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiSelectDropDownComponent;
